import { useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import { NewsRevenue } from '../../../types/naver.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: NewsRevenue[];
  startDate: Dayjs;
  endDate: Dayjs;
};

const GROUPS = [
  { rcType: '티어미디어', subtotalKey: 'tiermedia_total', cpTypes: ['뉴스톡', '하나머니'] as const },
  { rcType: '매거진', subtotalKey: 'magazine_total', cpTypes: ['에스콰이어', '바자', '엘르', '코스모폴리탄'] as const },
] as const;

const ALL_CP_TYPES = GROUPS.flatMap((g) => [...g.cpTypes]);

type PivotRow = {
  date: string;
  total: number;
  tiermedia_total: number;
  magazine_total: number;
  [key: string]: string | number;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);


export default function NewsRevenueTable({ data, startDate, endDate }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);

  const filename = useMemo(
    () => `news_revenue_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`,
    [startDate, endDate]
  );

  const pivotData = useMemo<PivotRow[]>(() => {
    const map = new Map<string, PivotRow>();
    data.forEach(({ date, cpType, revenue }) => {
      const cpSet = new Set<string>(ALL_CP_TYPES);
      if (!cpSet.has(cpType)) return;
      let entry = map.get(date);
      if (!entry) {
        const init: PivotRow = { date, total: 0, tiermedia_total: 0, magazine_total: 0 };
        ALL_CP_TYPES.forEach((cp) => { init[cp] = 0; });
        map.set(date, init);
        entry = init;
      }
      entry[cpType] = (entry[cpType] as number) + revenue;
      entry.total = (entry.total as number) + revenue;
      if ((GROUPS[0].cpTypes as readonly string[]).includes(cpType)) {
        entry.tiermedia_total = (entry.tiermedia_total as number) + revenue;
      } else if ((GROUPS[1].cpTypes as readonly string[]).includes(cpType)) {
        entry.magazine_total = (entry.magazine_total as number) + revenue;
      }
    });
    return Array.from(map.values());
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(pivotData);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu tableRef={tableRef} filename={filename} />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table ref={tableRef} size="small" sx={{ whiteSpace: 'nowrap', borderCollapse: 'collapse' }}>
          <TableHead>
            {/* 1행: 날짜 | 총매출 | rcType 그룹 */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell rowSpan={2} sortDirection={sortDirection} sx={{ verticalAlign: 'middle' }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell rowSpan={2} align="right" sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                총매출
              </TableCell>
              {GROUPS.map((g) => (
                <TableCell
                  key={g.rcType}
                  colSpan={g.cpTypes.length + 1}
                  align="center"
                  sx={{ fontWeight: 700, borderLeft: '2px solid #d0d0d0' }}
                >
                  {g.rcType}
                </TableCell>
              ))}
            </TableRow>
            {/* 2행: 합계 + cpType 칼럼명 */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {GROUPS.map((g) => (
                <>
                  <TableCell
                    key={`${g.rcType}_subtotal`}
                    align="right"
                    sx={{ fontSize: '0.75rem', fontWeight: 700, borderLeft: '2px solid #d0d0d0' }}
                  >
                    합계
                  </TableCell>
                  {g.cpTypes.map((cp) => (
                    <TableCell key={cp} align="right" sx={{ fontSize: '0.75rem' }}>
                      {cp}
                    </TableCell>
                  ))}
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatCurrency(row.total as number)}
                </TableCell>
                {GROUPS.map((g) => (
                  <>
                    <TableCell
                      key={`${g.rcType}_subtotal`}
                      align="right"
                      sx={{ fontWeight: 600, borderLeft: '2px solid #d0d0d0' }}
                    >
                      {(row[g.subtotalKey] as number) > 0
                        ? formatCurrency(row[g.subtotalKey] as number)
                        : '-'}
                    </TableCell>
                    {g.cpTypes.map((cp) => (
                      <TableCell key={cp} align="right">
                        {(row[cp] as number) > 0 ? formatCurrency(row[cp] as number) : '-'}
                      </TableCell>
                    ))}
                  </>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
